import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, Undo2 } from 'lucide-react';
import { HwDialog } from '../../views/HomeView/widgets/HwDialog';
import { useHabitDisciplinePrompt, type SupplementsTickRequest } from '../../../stores/habitDisciplinePromptStore';
import { useHabitStore } from '../../../stores/habitStore';
import { useSupplementStore } from '../../../stores/supplementStore';
import { useProgressDisplayStore } from '../../../stores/progressDisplayStore';
import { accountGeneration } from '../../../lib/accountStorage';
import { onSignOut } from '../../../lib/signOutReset';
import { toLocalYMD } from '../../../utils/hydration';
import { habitCountForDate, habitTarget, isSlotDoneForDate } from '../../../utils/habitProgress';
import { clockText, markOf, TIMES_OF_DAY, type DoseMark, type TimeOfDay } from '../../../utils/intakeSchedule';
import { isTimeOfDay } from '../../../utils/supplementsLink';
import type { Habit } from '../../../types';
import { buildToday, minuteNow, type TodayDose } from './todayModel';
import { PillGlyph } from './PillGlyph';
import { dayLabel } from './supplementsFormat';
import { useSupplementsPalette } from './supplementsPalette';
import { ACCOUNT_CHANGED, markAllTaken, markDoseLinked } from './markDoseLinked';

/**
 * ⭐ Global host for a tick of the supplements habit (owner, 2026-10-23 — step 6 of the
 * Supplements pass, the analogue of NutritionLogPromptHost; reviewed in the polish round,
 * 2026-10-24 — mock M4 "the habit tick prompt", gaps §7). Ticking the habit, or one of its
 * intervals, from ANY screen parks a request; this asks which doses were taken:
 *   · "Afternoon supplements · Thu 22" (a whole-day tick: "Supplements · …"; the date in
 *     Supplements' one style, `dayLabel`), and that time of day's doses due that day.
 *   · ⭐ EACH OPEN DOSE has its own Skip (56 × 44) and ✓ Taken (84 × 44) — the pair, order and
 *     size of Today's dock and list — and each press saves at once through Today's own mark
 *     (`markDoseLinked`): the habit then follows the record exactly as it does from Today — its
 *     interval ticks when every dose of it is recorded with at least one taken, and the dialog
 *     closes. A dose marked here keeps its row ("✓ Taken · Undo"); doses recorded before it
 *     opened fold into "RECORDED · 4" lines (Today's list changes those, never this).
 *   · ⭐ The bulk action stays, and says what it marks: "✓ Mark both taken", "✓ Mark all 4 taken
 *     · 2 are later today", "✓ Mark Omega-3 taken" — every OPEN dose, through Today's group mark
 *     (`markAllTaken` → lib/doseGroupMark), then the interval ticked under the link's operation
 *     id; a partial save says so ("1 of 2 saved · Try again for Omega-3").
 *   · A whole-day tick splits the open doses as Today counts them (to go = to take + later):
 *     "TO TAKE · 2" (NOW's time of day and earlier) and "LATER · 2".
 *   · ⭐ The subtitle never promises what the dialog can't do: "— mark what you took" only while a
 *     dose CAN be marked here (today's record, loaded, something open or marked here).
 *   · "Complete habit only" ticks without marking (its own operation id, never the link's);
 *     Cancel changes nothing — once something is marked here (a row's press, or a bulk mark that
 *     saved only some) it reads Close, named for what it does: "Close — your marks stay".
 *   · Nothing due then: a short confirm ("Nothing due this afternoon — complete the habit?").
 *   · A day that isn't today: the doses read only, and only "Complete habit only" — the record
 *     writes today only, and the dialog says so.
 * Every clock follows the app's 12/24-hour setting ("2 softgels · 1:00 PM").
 * A refused tick keeps the dialog ("Almost done") with Try again, which re-saves the SAME tick.
 * Unticking never comes here: it is direct, and every mark stays.
 * It wears Supplements' amethyst (`useSupplementsPalette`; HwDialog's `accent` for every focus
 * ring, the close X's too), whatever domain the habit is filed under; no red (Supplements' rule)
 * — a failure is said in the text colour; green only on the taken check. Mounted once at the
 * app root, next to NutritionLogPromptHost.
 */
export function SupplementsTickPromptHost() {
  const req = useHabitDisciplinePrompt(s => s.supplements);
  const clear = useHabitDisciplinePrompt(s => s.clearSupplements);
  // ⚠️ Another account signed in (or none): the last one's question goes with it.
  useEffect(() => onSignOut(() => useHabitDisciplinePrompt.getState().clearSupplements()), []);
  if (!req) return null;
  return <SupplementsTickDialog key={`${req.habitId}|${req.date}|${req.slotId}`} req={req} onDone={clear} />;
}

const LABEL: Record<TimeOfDay, string> = Object.fromEntries(TIMES_OF_DAY.map(t => [t.id, t.label])) as Record<TimeOfDay, string>;
const ORDER: TimeOfDay[] = TIMES_OF_DAY.map(t => t.id);

/** "this morning" · "at bedtime" · "today" — or, another day, "that morning" · "at bedtime that day" · "that day". */
function whenWords(part: TimeOfDay | 'day', today: boolean): string {
  if (part === 'day') return today ? 'today' : 'that day';
  if (part === 'bedtime') return today ? 'at bedtime' : 'at bedtime that day';
  return `${today ? 'this' : 'that'} ${part}`;
}
/** "Probiotic 2nd dose". */
const doseName = (d: TodayDose) => (d.nth ? `${d.item.name} ${d.nth}` : d.item.name);
/** "Creatine · Omega-3". */
const nameList = (ds: readonly TodayDose[]) => ds.map(doseName).join(' · ');

/** Is the asked-for interval (or, for a whole-day tick, the day) ticked on the habit? */
function askedTicked(h: Habit | undefined, date: string, slotId: string | null): boolean {
  if (!h) return false;
  return slotId == null ? habitCountForDate(h, date) >= habitTarget(h, date) : isSlotDoneForDate(h, date, slotId);
}

type Phase = 'choose' | 'tickFailed';
/** The press whose write is running — its button says "Saving…"; every other waits. */
type Busy = null | 'all' | 'tick' | { key: string; act: 'taken' | 'skipped' | 'undo' };

const EYEBROW = 'text-[11px] leading-4 font-semibold uppercase tracking-[.09em] text-muted-foreground';

function SupplementsTickDialog({ req, onDone }: { req: SupplementsTickRequest; onDone: () => void }) {
  const habit = useHabitStore(s => s.habits.find(h => h.id === req.habitId));
  const habitsUser = useHabitStore(s => s.initializedUserId);
  const stack = useSupplementStore(s => s.stack);
  const todayDate = useSupplementStore(s => s.todayDate);
  const todayTakenIds = useSupplementStore(s => s.todayTakenIds);
  const history = useSupplementStore(s => s.history);
  const dayTimes = useSupplementStore(s => s.dayTimes);
  const loadedUser = useSupplementStore(s => (s.isInitialized ? s.initializedUserId : null));
  const timeFormat = useProgressDisplayStore(s => s.timeFormat);
  const { accent, ink } = useSupplementsPalette();
  /** A tick made by hand ("Complete habit only"): its own operation id, never the link's. */
  const handOp = useRef(crypto.randomUUID()).current;
  /** The account the question was asked for. */
  const account = useRef(accountGeneration()).current;
  /** The clock when it opened — it only decides which doses are "to take" and which "later". */
  const [minute] = useState(minuteNow);
  /** Whether the asked-for interval was already ticked when it opened (then a later tick isn't news). */
  const [tickedAtOpen] = useState(() => askedTicked(habit, req.date, req.slotId));

  const [phase, setPhase] = useState<Phase>('choose');
  const [busy, setBusy] = useState<Busy>(null);
  const busyRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  /** After a refused tick: how the doses were marked ('all': by the bulk), and the tick to try again. */
  const [failed, setFailed] = useState<{ marked: 'all' | 'some' | false; opId: string } | null>(null);
  /** The doses pressed HERE, in order: they keep their row (with Undo); what else is recorded folds. */
  const [mine, setMine] = useState<string[]>([]);
  /** The last press here that SAVED, for the line ("Creatine saved as taken · 1 still open") — and
   *  the footer then reads Close: a mark made here stays. */
  const [last, setLast] = useState<{ key: string; mark: DoseMark } | null>(null);
  /** The bulk mark saved some doses before one was refused: they're recorded, so the footer reads Close too. */
  const [bulkSaved, setBulkSaved] = useState(false);
  /** Where focus goes once the press has re-drawn the rows. */
  const focusAfter = useRef<{ key: string; mark: DoseMark } | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const uid = useId();

  const today = toLocalYMD(new Date());
  const isToday = req.date === today;
  // The time of day the interval is (a whole-day tick: the day); null = an interval that isn't one.
  const part: TimeOfDay | 'day' | null = req.slotId == null ? 'day' : isTimeOfDay(req.slotId) ? req.slotId : null;
  // The record of that day, read only once the list has loaded for the habits' account.
  const loaded = loadedUser !== null && loadedUser === habitsUser;
  const ids = useMemo(() => (req.date === todayDate ? todayTakenIds : history.find(d => d.date === req.date)?.takenIds ?? []), [req.date, todayDate, todayTakenIds, history]);
  const model = useMemo(() => (loaded ? buildToday(stack, req.date, ids, minute, undefined, dayTimes, timeFormat) : null), [loaded, stack, req.date, ids, minute, dayTimes, timeFormat]);
  const doses = !model || !part ? [] : part === 'day' ? model.groups.flatMap(g => g.doses) : model.groups.find(g => g.timeOfDay === part)!.doses;
  const openDoses = doses.filter(d => d.mark === null);
  const open = openDoses.length;
  const nowIdx = model ? ORDER.indexOf(model.now) : 0;
  const isLater = (d: TodayDose) => ORDER.indexOf(d.dose.timeOfDay) > nowIdx;
  const laterOpen = openDoses.filter(isLater).length;
  /** Doses can be marked here: today's record, loaded, for a time of day (or the day). */
  const markable = isToday && loaded && req.date === todayDate && !!part;
  const canMark = markable && open > 0;

  const what = [req.habitName, req.slotLabel].filter(Boolean).join(' · ');
  const date = dayLabel(req.date, today);
  const title = part === 'day' ? `Supplements · ${date}`
    : part ? `${LABEL[part]} supplements · ${date}`
    : what;

  // After a press: focus the next OPEN dose's Taken (after this row, else the first); none left →
  // this row's Undo. After an Undo: this row's Taken. Never left on a button that went away.
  useEffect(() => {
    const f = focusAfter.current;
    const el = bodyRef.current;
    if (!f || !el || busy) return;
    focusAfter.current = null;
    const row = el.querySelector<HTMLElement>(`[data-dose="${CSS.escape(f.key)}"]`);
    if (f.mark === null) { row?.querySelector<HTMLElement>('[data-act="taken"]')?.focus(); return; }
    const takes = [...el.querySelectorAll<HTMLElement>('[data-act="taken"]')];
    const after = row ? takes.find(b => row.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) : undefined;
    (after ?? takes[0] ?? row?.querySelector<HTMLElement>('[data-act="undo"]'))?.focus();
  });

  /** The habit half, waited for — a refusal keeps the dialog with Try again (the SAME tick). */
  const tick = async (opId: string, marked: 'all' | 'some' | false) => {
    if (account !== accountGeneration()) { onDone(); return; }
    setBusy('tick'); setError(null);
    const r = await useHabitStore.getState().completeLinked(req.habitId, req.date, req.slotId, opId);
    setBusy(null);
    if (account !== accountGeneration()) { onDone(); return; }
    if (r.ok) { onDone(); return; }
    setFailed({ marked, opId });
    setPhase('tickFailed');
  };

  /** One dose, taken / skipped / back to not recorded — Today's own mark; the habit follows it. */
  const markOne = async (d: TodayDose, mark: DoseMark) => {
    if (busyRef.current || !markable || !part) return;
    const key = d.dose.key;
    busyRef.current = true;
    setBusy({ key, act: mark ?? 'undo' }); setError(null);
    // Its row stays put while it saves (the record changes under it), and after.
    const wasMine = mine.includes(key);
    if (!wasMine) setMine(m => [...m, key]);
    const r = await markDoseLinked({ key, mark, account });
    busyRef.current = false;
    if (account !== accountGeneration() || r.error === ACCOUNT_CHANGED) { onDone(); return; }
    setBusy(null);
    if (!r.marked) {
      if (!wasMine) setMine(m => m.filter(k => k !== key));
      setError(`${doseName(d)} didn’t save — check your connection, then press it again.`);
      return;
    }
    setLast({ key, mark });
    // The link ticked the asked-for interval (this press completed it): done.
    if (r.did === 'ticked' || (!tickedAtOpen && askedTicked(useHabitStore.getState().habits.find(h => h.id === req.habitId), req.date, req.slotId))) { onDone(); return; }
    if (!r.ok && r.error && r.opId && mark !== null) {
      // The dose saved; the habit's tick didn't — "Almost done", Try again re-saves that tick.
      setFailed({ marked: 'some', opId: r.opId });
      setPhase('tickFailed');
      return;
    }
    if (r.error) setError(r.error);
    focusAfter.current = { key, mark };
  };

  const markAll = async () => {
    if (busyRef.current || !canMark || !part) return;
    busyRef.current = true;
    setBusy('all'); setError(null);
    const wereOpen = openDoses.map(d => d.dose.key);
    const r = await markAllTaken({ habitId: req.habitId, part, slotId: req.slotId, account });
    busyRef.current = false;
    setBusy(null);
    if (account !== accountGeneration()) { onDone(); return; }
    if (r.ok) { onDone(); return; }
    if (r.marked) { setFailed({ marked: 'all', opId: r.opId }); setPhase('tickFailed'); return; }
    // A partial save ("1 of 2 saved · Try again for Omega-3"): what saved is recorded — closing keeps it.
    const ids = useSupplementStore.getState().todayTakenIds;
    if (wereOpen.some(k => markOf(ids, k) !== null)) setBulkSaved(true);
    setError(r.error ?? 'Couldn’t save — try again.');
  };

  if (!habit) return null;

  const primary = 'inline-flex items-center justify-center gap-1.5 h-11 px-3 rounded-lg text-sm font-semibold disabled:opacity-50';
  const secondary = 'inline-flex items-center justify-center h-11 px-3 rounded-lg border border-border text-sm font-semibold text-foreground disabled:opacity-50';
  const footBtn = 'inline-flex items-center justify-center h-11 sm:h-9 px-3 rounded-lg border border-border text-sm font-semibold text-foreground disabled:opacity-50';
  const solid = { backgroundColor: accent, color: ink };
  const partWord = part && part !== 'day' ? `${LABEL[part].toLowerCase()} ` : '';

  if (phase === 'tickFailed' && failed) {
    return (
      <HwDialog
        title="Almost done"
        subtitle={what}
        onClose={onDone}
        accent={accent}
        footer={(
          <>
            <button type="button" onClick={onDone} disabled={!!busy} className={footBtn}>Close</button>
            <button type="button" onClick={() => void tick(failed.opId, failed.marked)} disabled={!!busy} className={`${primary} sm:h-9`} style={solid}>{busy ? 'Saving…' : 'Try again'}</button>
          </>
        )}
      >
        <div className="space-y-2 text-sm" role="alert">
          {failed.marked === 'all' && <p className="text-foreground">Your {partWord}doses are marked taken.</p>}
          {failed.marked === 'some' && <p className="text-foreground">Your {partWord}doses are recorded.</p>}
          <p className="text-foreground">Ticking “{what}” didn’t save — check your connection. Try again ticks it; nothing is marked twice.</p>
          {failed.marked && <p className="text-xs text-muted-foreground">Closing keeps the doses marked and the habit unticked.</p>}
        </div>
      </HwDialog>
    );
  }

  const nothingDue = loaded && !!part && doses.length === 0;
  const handLabel = `Complete ${what} without marking any dose`;
  const who = part ? req.habitName : undefined;
  // ⭐ Only while a dose CAN be marked here does the subtitle say so.
  const subtitle = !who ? undefined : markable && (open > 0 || mine.length > 0) ? `${who} — mark what you took` : who;

  /** "1 scoop · 1:00 PM" — the units and the time (a plain time of day: its name, on a whole-day tick). */
  const subOf = (d: TodayDose) => {
    const units = d.amount.split(' · ')[0];
    const time = d.dose.time.at === 'slot' ? (part === 'day' ? LABEL[d.dose.timeOfDay] : '') : clockText(d.dose.minute, timeFormat);
    return [units, time].filter(Boolean).join(' · ');
  };

  // Markable: the rows are the open doses and those pressed here; the rest of what's recorded folds.
  const rows = markable ? doses.filter(d => d.mark === null || mine.includes(d.dose.key)) : doses;
  const folded = markable ? doses.filter(d => d.mark !== null && !mine.includes(d.dose.key)) : [];
  // A whole-day tick splits them as Today counts them (TO TAKE = NOW's time of day and earlier ·
  // LATER); one time of day is one list — headed only when RECORDED sits above it.
  const sections: { id: string; label: string | null; rows: TodayDose[] }[] = !markable
    ? [{ id: 'all', label: null, rows }]
    : part === 'day'
      ? [{ id: 'take', label: 'To take', rows: rows.filter(d => !isLater(d)) }, { id: 'later', label: 'Later', rows: rows.filter(isLater) }].filter(s => s.rows.length)
      : [{ id: 'take', label: !folded.length ? null : rows.some(isLater) ? 'Later' : 'To take', rows }];

  // The line under the rows: what a press does, or what the last one did and what is left.
  // The link's rule (every dose recorded, at least one taken), said for this list: first as a rule,
  // then, after a press here, as what is left.
  const taken = doses.filter(d => d.mark === 'taken').length;
  const one = <b className="font-semibold text-foreground">{openDoses[0] ? doseName(openDoses[0]) : ''}</b>;
  const rule = doses.length === 1 ? 'when it’s taken.'
    : `when ${doses.length === 2 ? 'both are' : `all ${doses.length} are`} recorded, with at least one taken.`;
  const left = taken > 0
    ? (open === 1 ? <>when {one} is recorded too.</> : `when the other ${open} are recorded too.`)
    : (open === 1 ? <>when {one} is taken.</> : `when the other ${open} are recorded, with at least one taken.`);
  const lastDose = last ? doses.find(d => d.dose.key === last.key) : undefined;
  const line = !markable || !part ? null
    : open === 0
      ? <>Every dose {whenWords(part, true)} is recorded.{taken === 0 ? ' None was taken, so the habit isn’t ticked — Complete habit ticks it anyway.' : ''}</>
      : lastDose && last
        ? <><b className="font-semibold text-foreground">{doseName(lastDose)}</b> {last.mark ? `saved as ${last.mark}` : 'is back to not recorded'} · {open} still open. The habit ticks {last.mark ? left : rule}</>
        : <>Each press saves at once. <b className="font-semibold text-foreground">{what}</b> ticks {rule}</>;

  const bulk = !canMark ? null
    : open === 1 ? `Mark ${doseName(openDoses[0])} taken` : open === 2 ? 'Mark both taken' : `Mark all ${open} taken`;
  const bulkLater = laterOpen === 0 ? '' : laterOpen === 1 ? '1 is later today' : `${laterOpen} are later today`;

  return (
    <HwDialog
      title={title}
      subtitle={subtitle}
      onClose={onDone}
      accent={accent}
      /* Once something is recorded here it reads Close, and says what closing does (the X is "Close" too). */
      footer={(
        <button type="button" onClick={onDone} disabled={busy === 'all' || busy === 'tick'} className={footBtn}
          aria-label={last || bulkSaved ? 'Close — your marks stay' : undefined}>{last || bulkSaved ? 'Close' : 'Cancel'}</button>
      )}
    >
      <div ref={bodyRef} className="space-y-3">
        {!loaded ? (
          <p className="text-sm text-muted-foreground" role="status">Loading your supplements…</p>
        ) : !part ? (
          <p className="text-sm text-muted-foreground">This interval isn’t one of your times of day.</p>
        ) : nothingDue ? (
          <p className="text-sm text-foreground">Nothing due {whenWords(part, isToday)} — complete the habit?</p>
        ) : (
          <>
            {folded.length > 0 && <RecordedFold doses={folded} />}
            {sections.map(s => (
              <div key={s.id} data-section={s.id}>
                {s.label && (
                  <h3 className={`${EYEBROW} mb-1`} id={`${uid}-${s.id}`}>
                    {s.label} · <span className="tabular-nums">{s.rows.filter(d => d.mark === null).length}</span>
                  </h3>
                )}
                <ul aria-labelledby={s.label ? `${uid}-${s.id}` : undefined}
                  aria-label={s.label ? undefined : `${part === 'day' ? 'Doses' : `${LABEL[part]} doses`} due ${isToday ? 'today' : date}`}>
                  {s.rows.map(d => (
                    <PromptRow key={d.dose.key} d={d} sub={subOf(d)} markable={markable} today={isToday} busy={busy}
                      onMark={mark => void markOne(d, mark)} />
                  ))}
                </ul>
              </div>
            ))}
            {!isToday && <p className="text-xs text-muted-foreground">Doses can only be marked for today, so this ticks the habit only.</p>}
            {line && <p className="text-xs leading-[18px] text-muted-foreground border-t border-border/70 pt-3" aria-live="polite">{line}</p>}
          </>
        )}

        <div className="flex flex-col gap-2">
          {canMark && bulk ? (
            <>
              <button type="button" onClick={() => void markAll()} disabled={!!busy} className={primary} style={solid}
                aria-label={`${bulk}${bulkLater ? ` · ${bulkLater}` : ''}, then complete ${what}`}>
                {busy === 'all' ? 'Saving…' : (
                  <>
                    <Check className="w-4 h-4" strokeWidth={2.6} aria-hidden="true" /> {bulk}
                    {bulkLater && <span className="font-normal opacity-80">· {bulkLater}</span>}
                  </>
                )}
              </button>
              <button type="button" onClick={() => void tick(handOp, false)} disabled={!!busy} className={secondary} aria-label={handLabel}>Complete habit only</button>
            </>
          ) : (
            <button type="button" onClick={() => void tick(handOp, false)} disabled={!!busy} className={primary} style={solid}
              aria-label={nothingDue ? `Complete ${what}` : handLabel}>
              {busy === 'tick' ? 'Saving…' : nothingDue || (markable && open === 0) ? 'Complete habit' : 'Complete habit only'}
            </button>
          )}
        </div>
        {error && <p className="text-sm text-foreground" role="alert">{error}</p>}
      </div>
    </HwDialog>
  );
}

/**
 * One dose. Open, on today's record: Skip (56 × 44) and ✓ Taken (84 × 44), outlined — the dialog's
 * one solid button is the bulk action. Marked here: its mark and Undo (72 × 44). Read only (another
 * day): its mark in words.
 */
function PromptRow({ d, sub, markable, today, busy, onMark }: {
  d: TodayDose; sub: string; markable: boolean; today: boolean; busy: Busy; onMark: (mark: DoseMark) => void;
}) {
  const name = doseName(d);
  const mine = busy && typeof busy === 'object' && busy.key === d.dose.key ? busy.act : null;
  const btn = 'inline-flex items-center justify-center gap-1.5 h-11 rounded-lg text-[13px] font-semibold disabled:opacity-60';
  return (
    <li className="flex items-center gap-3 py-2 border-t border-border/70 first:border-t-0" data-dose={d.dose.key} data-mark={d.mark ?? 'open'}
      aria-busy={mine ? true : undefined}>
      <PillGlyph shape={d.shape} size={28} quiet={d.mark !== null} />
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-semibold text-foreground truncate" title={name}>{name}</span>
        {sub && <span className="block text-xs text-muted-foreground truncate">{sub}</span>}
      </span>
      {!markable ? (
        <MarkWord mark={d.mark} today={today} />
      ) : d.mark === null ? (
        <span className="flex items-center gap-2 shrink-0">
          <button type="button" data-act="skipped" disabled={!!busy} onClick={() => onMark('skipped')} aria-label={`Skip ${name} today`}
            className={`${btn} w-14 border border-border text-muted-foreground hover:text-foreground`}>
            {mine === 'skipped' ? 'Saving…' : 'Skip'}
          </button>
          <button type="button" data-act="taken" disabled={!!busy} onClick={() => onMark('taken')} aria-label={`Mark ${name} taken`}
            className={`${btn} w-[84px] border border-foreground/25 text-foreground hover:border-foreground/45`}>
            {mine === 'taken' ? 'Saving…' : <><Check className="w-3.5 h-3.5" strokeWidth={2.6} aria-hidden="true" />Taken</>}
          </button>
        </span>
      ) : (
        <span className="flex items-center gap-2 shrink-0">
          <MarkWord mark={d.mark} today={today} />
          <button type="button" data-act="undo" disabled={!!busy} onClick={() => onMark(null)} aria-label={`Undo ${name} ${d.mark}`}
            className={`${btn} w-[72px] border border-border text-muted-foreground hover:text-foreground`}>
            {mine === 'undo' ? 'Saving…' : <><Undo2 className="w-3.5 h-3.5" aria-hidden="true" />Undo</>}
          </button>
        </span>
      )}
    </li>
  );
}

/** "RECORDED · 4" — the doses recorded before the dialog opened, in two lines, as Today's done group folds. */
function RecordedFold({ doses }: { doses: TodayDose[] }) {
  const taken = doses.filter(d => d.mark === 'taken');
  const skipped = doses.filter(d => d.mark === 'skipped');
  return (
    <div data-section="recorded">
      <h3 className={`${EYEBROW} mb-1`}>Recorded · <span className="tabular-nums">{doses.length}</span></h3>
      <div className="space-y-1 text-[13px] leading-5">
        {taken.length > 0 && (
          <p className="flex items-start gap-2" data-recorded="taken"><TakenDot /><span className="min-w-0"><span className="text-muted-foreground">Taken: </span><b className="font-semibold text-foreground">{nameList(taken)}</b></span></p>
        )}
        {skipped.length > 0 && (
          <p className="flex items-start gap-2" data-recorded="skipped"><SkipDot /><span className="min-w-0"><span className="text-muted-foreground">Skipped: </span><b className="font-semibold text-foreground">{nameList(skipped)}</b></span></p>
        )}
      </div>
    </div>
  );
}

/** The small green taken check — green only here. */
function TakenDot() {
  return (
    <span aria-hidden="true" className="mt-[3px] inline-grid place-items-center w-3.5 h-3.5 shrink-0 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
      <Check className="w-2.5 h-2.5" strokeWidth={3.4} />
    </span>
  );
}
/** The skipped ring: dashed, no colour. */
function SkipDot() {
  return <span aria-hidden="true" className="mt-[3px] inline-block w-3.5 h-3.5 shrink-0 rounded-full border border-dashed border-muted-foreground" />;
}

/** A dose's mark in words: ✓ Taken (the small green check) · ◌ Skipped · Not yet / Not recorded. */
function MarkWord({ mark, today }: { mark: TodayDose['mark']; today: boolean }) {
  if (mark === 'taken') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
        <span aria-hidden="true" className="inline-grid place-items-center w-3.5 h-3.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
          <Check className="w-2.5 h-2.5" strokeWidth={3.4} />
        </span>
        Taken
      </span>
    );
  }
  if (mark === 'skipped') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
        <span aria-hidden="true" className="inline-block w-3.5 h-3.5 rounded-full border border-dashed border-muted-foreground" />
        Skipped
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground shrink-0">{today ? 'Not yet' : 'Not recorded'}</span>;
}
